import { cn } from '@heidi/utils';
import { Slot } from '@radix-ui/react-slot';
// Package-internal seam, deliberately not on the button door — see `../button/index.ts`.
import { useButtonProps } from '../button/use-button-props';

import { skipLinkBaseClasses, skipLinkTargetFocusClasses } from './skip-link.styles';
import type { SkipLinkProps } from './skip-link.types';

/**
 * Returns props for a skip link target element.
 * Apply these props to the element you want skip links to navigate to.
 *
 * The focus indicator is an inset outline whose radius inherits from the
 * direct parent (`rounded-[inherit]`), so it automatically hugs the container
 * it sits in — rounded or square. A target that owns its own radius overrides
 * by passing `rounded-*` via `className`.
 *
 * @param id - The id that will be referenced by the skip link's href (without #)
 * @param className - Optional additional classes to merge with the focus styles
 *
 * @example
 * ```tsx
 * <main {...skipLinkTargetProps('main-content', 'container mx-auto')}>
 *   {content}
 * </main>
 * ```
 */
export const skipLinkTargetProps = (id: string, className?: string) => ({
  id,
  tabIndex: -1 as const,
  className: cn(skipLinkTargetFocusClasses, className),
});

const SkipLink = (props: SkipLinkProps) => {
  const { children, className, asChild = false, ...restProps } = props;
  const Comp = asChild ? Slot : 'a';
  // Resolved the same way `Button` does, so the skip link tracks
  // `release-experimental-design-system-forms` rather than staying pinned to the
  // stable recipe (DS-1575). `sm` is explicit because the experimental recipe
  // defaults to `md` (DS-1453).
  //
  // Class string only: this renders an `<a>`, and the `data-slot="button"` marker
  // would make button-scoped selectors (`button-group.styles.ts`) match a link.
  const { className: buttonClasses } = useButtonProps({
    variant: 'secondary',
    size: 'sm',
  });

  return (
    <Comp
      {...(!asChild && 'href' in props ? { href: props.href } : {})}
      className={cn(buttonClasses, skipLinkBaseClasses, className)}
      {...restProps}
    >
      {children}
    </Comp>
  );
};

export default SkipLink;
